import { AM_INITIAL, isDayPeriodChar, isDigit, TIME_FIELDS } from './pattern-token.constants';
import { DECIMAL_BASE, FULL_YEAR_DIGITS } from './date-picker.constants';
import {
  daysInMonth,
  expandShortYear,
  parseLoose,
  parseLooseRange,
  parseSlots,
} from './value-parser';
import {
  blankSlot,
  caretAt,
  emptySlots,
  fieldAtPosition,
  fieldForInput,
  fieldsInRange,
  isSlotBlank,
  nextField,
  normalizeSlot,
  prevField,
  selectField,
  slotsFromDisplay,
  slotsFromValues,
  writeSlot,
} from './slot-layout';
import {
  ALL_SIDES,
  Caret,
  CompiledPattern,
  DayPeriod,
  EditOptions,
  EditResult,
  EMPTY_FIELD_VALUES,
  FieldKind,
  FieldPart,
  FieldValues,
  ParseIssue,
  PatternPartKind,
  RangeSide,
  SideSelector,
} from './date-picker.model';

/**
 * Every editing primitive is a pure, total reducer over slots: it never throws and
 * never touches the DOM. The old implementation threw from inside a keystroke handler
 * whenever the caret drifted, which made the mask a partial function.
 */

export const DeleteDirection = { Back: 'back', Forward: 'forward', Word: 'word' } as const;
export type DeleteDirection = (typeof DeleteDirection)[keyof typeof DeleteDirection];

export const ArrowDirection = { Up: 'up', Down: 'down' } as const;
export type ArrowDirection = (typeof ArrowDirection)[keyof typeof ArrowDirection];

/** Which edge of the day a blank time is filled with. */
export const TimeEdge = { From: 'from', To: 'to' } as const;
export type TimeEdge = (typeof TimeEdge)[keyof typeof TimeEdge];

/** Characters a slot holds before and after the user has typed into it. */
const BLANK_CHAR = ' ';
const ZERO_CHAR = '0';

/** Fill for a blank time slot, by field and edge of the day. */
const HOUR24_EDGE = { from: '00', to: '23' } as const;
const HOUR12_EDGE = { from: '12', to: '11' } as const;
const MINUTE_EDGE = { from: '00', to: '59' } as const;

export function applyChar(
  pattern: CompiledPattern,
  slots: readonly string[],
  caret: Caret,
  char: string,
  options: EditOptions,
): EditResult {
  const [start, end] = caret;
  const working = start === end ? slots : clearRange(pattern, slots, start, end);

  const target = fieldForInput(pattern, start);
  if (!target) return { slots: working, caret };

  if (isSeparator(char, pattern, options)) {
    return commitAndAdvance(pattern, working, target.field, target.offset, caret);
  }

  if (target.field.field === FieldKind.DayPeriod) {
    if (!isDayPeriodChar(char)) return { slots: working, caret };
    const value = char.toLowerCase() === AM_INITIAL ? DayPeriod.Am : DayPeriod.Pm;
    return advanceAfterWrite(pattern, writeSlot(working, target.field.index, value), target.field);
  }

  if (!isDigit(char)) return { slots: working, caret };

  return applyDigit(pattern, working, target.field, target.offset, char);
}

export function applyDelete(
  pattern: CompiledPattern,
  slots: readonly string[],
  caret: Caret,
  direction: DeleteDirection,
): EditResult {
  const [start, end] = caret;
  const forward = direction === DeleteDirection.Forward;

  if (start !== end) {
    const touched = fieldsInRange(pattern, start, end);
    const hasContent = touched.some((field) => !isSlotBlank(slots[field.index] ?? ''));

    // A selection with anything in it is simply cleared.
    if (hasContent) {
      const working = touched.reduce(
        (acc, field) => writeSlot(acc, field.index, blankSlot(field.width)),
        slots,
      );
      return { slots: working, caret: selectField(touched[0]!) };
    }

    // Clearing a field leaves it selected, so a second press arrives here with an
    // already-empty selection. Treat it as a delete at the selection edge and walk on
    // to the neighbour, instead of clearing the same empty field forever.
  }

  const anchor = start !== end ? (forward ? end : start) : start;

  const field = forward
    ? (fieldAtPosition(pattern, anchor) ?? nextField(pattern, anchor - 1))
    : (fieldAtPosition(pattern, anchor - 1) ?? prevField(pattern, anchor));

  if (!field) return { slots, caret };

  return {
    slots: writeSlot(slots, field.index, blankSlot(field.width)),
    caret: selectField(field),
  };
}

export function applyArrow(
  pattern: CompiledPattern,
  slots: readonly string[],
  caret: Caret,
  direction: ArrowDirection,
  fallback: FieldValues = EMPTY_FIELD_VALUES,
): EditResult {
  const target = fieldForInput(pattern, caret[0]);
  if (!target) return { slots, caret };

  const { field } = target;

  if (field.field === FieldKind.DayPeriod) {
    const current = (slots[field.index] ?? '').trim().toLowerCase();
    return {
      slots: writeSlot(slots, field.index, current === DayPeriod.Am ? DayPeriod.Pm : DayPeriod.Am),
      caret: selectField(field),
    };
  }

  const raw = (slots[field.index] ?? '').trim();
  let value = raw
    ? Number(raw) + (direction === ArrowDirection.Up ? 1 : -1)
    : fallbackValue(field, fallback);

  if (Number.isNaN(value)) value = field.min;
  if (value > field.max) value = field.min;
  if (value < field.min) value = field.max;

  return {
    slots: writeSlot(slots, field.index, String(value).padStart(field.width, ZERO_CHAR)),
    caret: selectField(field),
  };
}

/**
 * Groups enough for both ends of a range fill the whole range; anything shorter fills the
 * side the caret is in. A range pasted back-to-front is put in order right away — the
 * blur-time `orderRangeSides` would leave it reversed on screen until focus moved.
 */
export function applyPaste(
  pattern: CompiledPattern,
  slots: readonly string[],
  caret: Caret,
  text: string,
): EditResult {
  const range = pattern.isRange ? parseLooseRange(text, pattern) : null;

  if (range) {
    const filled = orderRangeSides(
      pattern,
      fillSide(
        pattern,
        fillSide(pattern, slots, range.from, RangeSide.From),
        range.to,
        RangeSide.To,
      ),
    );

    return { slots: filled, caret: caretAfterFill(pattern, filled) };
  }

  const values = parseLoose(text, pattern);
  if (!values) return { slots, caret };

  const target = fieldForInput(pattern, caret[0]);
  const side = pattern.isRange ? (target?.field.side ?? RangeSide.From) : ALL_SIDES;
  const working = fillSide(pattern, slots, values, side);

  return { slots: working, caret: caretAfterFill(pattern, working) };
}

function fillSide(
  pattern: CompiledPattern,
  slots: readonly string[],
  values: Partial<FieldValues>,
  side: SideSelector,
): readonly string[] {
  const merged: FieldValues = { ...EMPTY_FIELD_VALUES, ...values };
  return slotsFromValues(pattern, slots, merged, side, false);
}

/**
 * Recovery path for input the mask deliberately does not intercept — undo/redo, IME
 * composition, autofill. The browser has already mutated the DOM; this reads whatever
 * it produced back into slots so the `[value]` binding can snap the field to canonical
 * form on the same tick.
 */
export function reconcile(pattern: CompiledPattern, rawValue: string): EditResult {
  const positional = slotsFromDisplay(pattern, rawValue);
  if (positional) {
    return { slots: positional, caret: caretAfterFill(pattern, positional) };
  }

  if (!rawValue.trim()) {
    const empty = emptySlots(pattern);
    return { slots: empty, caret: caretAt(pattern.fields[0]?.start ?? 0) };
  }

  const values = parseLoose(rawValue, pattern);
  if (!values) {
    const empty = emptySlots(pattern);
    return { slots: empty, caret: caretAt(pattern.fields[0]?.start ?? 0) };
  }

  const merged: FieldValues = { ...EMPTY_FIELD_VALUES, ...values };
  const working = slotsFromValues(pattern, emptySlots(pattern), merged, ALL_SIDES, true);

  return { slots: working, caret: caretAfterFill(pattern, working) };
}

function applyDigit(
  pattern: CompiledPattern,
  slots: readonly string[],
  field: FieldPart,
  offset: number,
  digit: string,
): EditResult {
  const result = writeDigit(field, slots[field.index] ?? '', offset, digit);
  const working = writeSlot(slots, field.index, result.slot);

  if (result.carry) {
    const following = nextField(pattern, field.start);
    if (!following) return { slots: working, caret: caretAt(field.end) };
    return applyDigit(pattern, working, following, 0, result.carry);
  }

  if (result.offset >= field.width) {
    return advanceAfterWrite(pattern, working, field);
  }

  return { slots: working, caret: caretAt(field.start + result.offset) };
}

/**
 * Overflow-carry rule, ported from the old segment closures but data-driven and total:
 * a first digit too large for the field completes it zero-padded, and a second digit
 * that would exceed the maximum spills into the next field instead of being dropped.
 */
function writeDigit(
  field: FieldPart,
  slot: string,
  offset: number,
  digit: string,
): { slot: string; offset: number; carry: string | null } {
  const current = normalizeSlot(slot, field.width);

  if (field.width > 2) {
    if (offset < 0 || offset >= field.width) {
      return { slot: current, offset: field.width, carry: null };
    }
    const chars = [...current];
    chars[offset] = digit;
    return { slot: chars.join(''), offset: offset + 1, carry: null };
  }

  const maxFirstDigit = Math.floor(field.max / DECIMAL_BASE);
  const value = Number(digit);

  if (offset <= 0) {
    if (value <= maxFirstDigit) {
      return { slot: digit + current[1], offset: 1, carry: null };
    }
    return { slot: `0${digit}`, offset: 2, carry: null };
  }

  const firstChar = current[0] === BLANK_CHAR ? ZERO_CHAR : current[0];
  const candidate = Number(`${firstChar}${digit}`);

  if (Number(firstChar) < maxFirstDigit || candidate <= field.max) {
    const clamped = candidate < field.min ? String(field.min).padStart(2, '0') : firstChar + digit;
    return { slot: clamped, offset: 2, carry: null };
  }

  return { slot: `0${firstChar}`, offset: 2, carry: digit };
}

/**
 * A separator commits the field being typed and moves on: `1/2/2026` becomes
 * `01/02/2026`. Typing it at the start of a field nobody has touched is a no-op —
 * the user is just retyping the separator that is already on screen, and skipping
 * the field would silently swallow it.
 */
function commitAndAdvance(
  pattern: CompiledPattern,
  slots: readonly string[],
  field: FieldPart,
  offset: number,
  caret: Caret,
): EditResult {
  const slot = normalizeSlot(slots[field.index] ?? '', field.width);

  if (offset <= 0 && isSlotBlank(slot)) return { slots, caret };

  return advanceAfterWrite(pattern, writeSlot(slots, field.index, commitSlot(field, slot)), field);
}

/**
 * Canonical text for a field the caret is leaving: `1/` reads as `01`, and a year the
 * user only half-typed gets its century — `26` becomes `2026`. Three digits are already
 * a year of their own, so `120` stays 120; only the century is ever filled in.
 */
function commitSlot(field: FieldPart, slot: string): string {
  const normalized = normalizeSlot(slot, field.width);
  if (isSlotBlank(normalized)) return normalized;

  if (field.field === FieldKind.Year && field.width >= FULL_YEAR_DIGITS) {
    // Not `trim()`: clicking mid-field can leave a gap inside the slot.
    const digits = normalized.replace(/ /g, '');
    return digits.length === field.width
      ? normalized
      : String(expandShortYear(Number(digits))).padStart(field.width, ZERO_CHAR);
  }

  if (field.width !== 2 || field.field === FieldKind.DayPeriod) return normalized;

  const padded = normalized.trim().padStart(2, '0');

  // `0` alone is not a month or a day; settle it to the field's floor rather than
  // leaving `00` behind for the validator to reject.
  return Number(padded) < field.min ? String(field.min).padStart(2, '0') : padded;
}

/**
 * Completes every half-typed field except the one at `exceptIndex`, which is where the
 * caret is landing — the user is still typing that one. Returns the original array when
 * nothing changed, so a caller can skip re-emitting.
 *
 * Applies to all fields, not just years: leaving a month holding `1` should settle it to
 * `01` exactly as a year holding `1` settles to `2001`.
 */
export function commitPartialFields(
  pattern: CompiledPattern,
  slots: readonly string[],
  exceptIndex: number | null = null,
): readonly string[] {
  let next = slots;

  for (const field of pattern.fields) {
    if (field.index === exceptIndex) continue;

    const slot = slots[field.index] ?? '';
    const committed = commitSlot(field, slot);
    if (committed !== slot) next = writeSlot(next, field.index, committed);
  }

  return next;
}

/**
 * Clamps a day that overflows its month to the last day of that month — `31.06` settles
 * to `30.06`, `31.02.2004` to `29.02.2004`.
 *
 * Someone typing `31.06` has misremembered the length of June, not the month they meant,
 * so the month is what to keep. Rolling forward into July would silently answer a
 * different question.
 *
 * It also keeps one answer to "does this date exist": `formatOutput` builds its own
 * `Date` from the raw values and JS rolls an overflow over without complaint, so before
 * this the field showed `31.02.2004` and flagged it invalid while the form already held
 * `2004-03-02`.
 *
 * Call it on blur only: doing it while the caret is still in the date would rewrite the
 * day out from under someone who has typed `31` and not yet corrected the month.
 */
export function clampOverflowDays(
  pattern: CompiledPattern,
  slots: readonly string[],
): readonly string[] {
  const sides: SideSelector[] = pattern.isRange ? [RangeSide.From, RangeSide.To] : [ALL_SIDES];
  let next = slots;

  for (const side of sides) {
    const { values, issues } = parseSlots(pattern, next, side);
    if (!issues.includes(ParseIssue.InvalidDay)) continue;
    if (values.year === null || values.month === null || values.day === null) continue;

    next = slotsFromValues(
      pattern,
      next,
      { ...values, day: daysInMonth(values.year, values.month) },
      side,
      false,
    );
  }

  return next;
}

/**
 * Fills the time parts the user left blank with the edge of the day: `9` settles to
 * `09:00` for a `from` field and `09:59` for a `to` one, and an untouched hour becomes
 * `00`/`23`.
 *
 * Without this a field the user genuinely finished — a pickup at 9 — stays `incomplete`
 * and reports `invalid`, because the minute slot is still blank. The edge is the answer
 * the two modes already imply: a lower bound starts the minute, an upper bound ends it.
 *
 * A field nobody has typed in is left alone: an empty control must not acquire a time
 * just because the caret passed through it.
 *
 * Blur only, and after `commitPartialFields` — a half-typed `9` has to become `09`
 * before the blanks around it are read.
 */
export function autocompleteTime(
  pattern: CompiledPattern,
  slots: readonly string[],
  edge: TimeEdge,
): readonly string[] {
  if (!pattern.hasTime) return slots;
  if (pattern.fields.every((field) => isSlotBlank(slots[field.index] ?? ''))) return slots;

  const isBlank = (field: FieldPart) => isSlotBlank(slots[field.index] ?? '');
  const hourWasBlank = pattern.fields
    .filter((field) => TIME_FIELDS.has(field.field))
    .every(isBlank);

  let next = slots;

  for (const field of pattern.fields) {
    if (!TIME_FIELDS.has(field.field) || !isBlank(field)) continue;

    switch (field.field) {
      case FieldKind.Hour24:
        next = writeSlot(next, field.index, HOUR24_EDGE[edge]);
        break;
      case FieldKind.Hour12:
        next = writeSlot(next, field.index, HOUR12_EDGE[edge]);
        break;
      case FieldKind.Minute:
        next = writeSlot(next, field.index, MINUTE_EDGE[edge]);
        break;
      // An hour the picker filled itself carries the matching half of the day. An hour the
      // user typed does not say which half they meant, and `am` is the old picker's answer.
      case FieldKind.DayPeriod:
        next = writeSlot(
          next,
          field.index,
          hourWasBlank && edge === TimeEdge.To ? DayPeriod.Pm : DayPeriod.Am,
        );
        break;
      // Skipped by the TIME_FIELDS check above.
      case FieldKind.Year:
      case FieldKind.Month:
      case FieldKind.Day:
        break;
    }
  }

  return next;
}

/**
 * Puts a reversed range back in order: `20.05.2026 – 01.01.2020` settles to
 * `01.01.2020 – 20.05.2026`.
 *
 * The calendar path orders the two ends as it writes them, so this is the typing path's
 * equivalent — without it a reversed range reads as complete and reaches the form as-is.
 *
 * Call it on blur only, and after `clampOverflowDays`: swapping while the caret is still
 * in the range would move the field out from under someone who typed the end date first.
 */
export function orderRangeSides(
  pattern: CompiledPattern,
  slots: readonly string[],
): readonly string[] {
  if (!pattern.isRange) return slots;

  const from = parseSlots(pattern, slots, RangeSide.From);
  const to = parseSlots(pattern, slots, RangeSide.To);

  if (!from.date || !to.date || from.date.getTime() <= to.date.getTime()) return slots;

  return slotsFromValues(
    pattern,
    slotsFromValues(pattern, slots, to.values, RangeSide.From, false),
    from.values,
    RangeSide.To,
    false,
  );
}

function advanceAfterWrite(
  pattern: CompiledPattern,
  slots: readonly string[],
  field: FieldPart,
): EditResult {
  const following = nextField(pattern, field.start);
  return { slots, caret: following ? selectField(following) : caretAt(field.end) };
}

function clearRange(
  pattern: CompiledPattern,
  slots: readonly string[],
  start: number,
  end: number,
): readonly string[] {
  return fieldsInRange(pattern, start, end).reduce(
    (acc, field) => writeSlot(acc, field.index, blankSlot(field.width)),
    slots,
  );
}

function isSeparator(char: string, pattern: CompiledPattern, options: EditOptions): boolean {
  if (options.acceptedSeparators.includes(char)) return true;
  return pattern.parts.some(
    (part) => part.kind === PatternPartKind.Literal && part.text.includes(char),
  );
}

function fallbackValue(field: FieldPart, fallback: FieldValues): number {
  switch (field.field) {
    case FieldKind.Year:
      return fallback.year ?? field.min;
    case FieldKind.Month:
      return fallback.month ?? field.min;
    case FieldKind.Day:
      return fallback.day ?? field.min;
    case FieldKind.Hour24:
      return fallback.hour24 ?? field.min;
    case FieldKind.Hour12:
      return fallback.hour12 ?? field.min;
    case FieldKind.Minute:
      return fallback.minute ?? field.min;
    case FieldKind.DayPeriod:
      return field.min;
  }
}

/** Selects the first field still blank, so the user is pointed at what is missing. */
function caretAfterFill(pattern: CompiledPattern, slots: readonly string[]): Caret {
  const blank = pattern.fields.find((field) => isSlotBlank(slots[field.index] ?? ''));
  return blank ? selectField(blank) : caretAt(pattern.length);
}
